import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ChevronRight, Settings, Clock, ChevronDown, Flame, Store, ImageOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ProductCard } from '../../components/shopper/ProductCard';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { FASH_LOGO } from '../../constants/brand';
import { SearchOverlay } from '../../components/shopper/SearchOverlay';
import { LocationPickerSheet } from '../../components/shopper/LocationPickerSheet';
import { useBoutiques, useFeaturedBoutiques } from '../../hooks/useBoutiques';
import { useProducts } from '../../hooks/useProducts';
import { useShopperLocation } from '../../hooks/useShopperLocation';
import { formatMiles } from '../../lib/geo';
import {
  FeaturedStoreHeroCarousel,
} from '../../components/shopper/FeaturedStoreHeroCarousel';
import { ShopperBottomNav } from '../../components/shopper/ShopperBottomNav';
import { ShopperFooter } from '../../components/shopper/ShopperFooter';
import { SHOPPER_HOME } from '../../constants/routes';

const MOBILE_PRODUCT_COUNT = 4;

function FallbackImg({ src, alt, className, fallbackIcon }: { src: string; alt: string; className: string; fallbackIcon?: 'store' | 'image' }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-200`}>
        {fallbackIcon === 'image' ? (
          <ImageOff className="h-8 w-8 text-gray-400" />
        ) : (
          <Store className="h-8 w-8 text-gray-400" />
        )}
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
}

export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const cart = useCart();
  const favorites = useFavoritesContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [showAllMobile, setShowAllMobile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const {
    location: shopperLocation,
    label: locationLabel,
    loading: locationLoading,
    hasCoords,
  } = useShopperLocation();

  const near =
    hasCoords && shopperLocation
      ? { lat: shopperLocation.lat as number, lng: shopperLocation.lng as number }
      : null;

  const { boutiques, loading: boutiquesLoading } = useBoutiques({ limitCount: 10, near });
  const { boutiques: featuredBoutiques, loading: featuredLoading } = useFeaturedBoutiques();
  const { products: trendingProducts, loading: productsLoading } = useProducts({ limitCount: 12 });

  const filters = ['All', 'Dresses', 'Tops', 'Bottoms'];

  const handleProductClick = (id: string) => {
    navigate(`/product/${id}`, { state: { from: location.pathname } });
  };

  const handleBoutiqueClick = (id: string) => {
    navigate(`/boutique/${id}`, { state: { from: location.pathname } });
  };

  const handleAddToCart = (productId: string, selectedSize?: string) => {
    const product = trendingProducts.find(p => p.id === productId);
    if (!product) return;

    const size = product.sizes?.find((item) => item.label === selectedSize && item.available);
    if (!size) {
      navigate(`/product/${product.id}`);
      return;
    }

    cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId,
      price: product.price,
      size: size.label,
      quantity: 1,
      imageUrl: product.images?.[0] || '',
    });
  };

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {/* Search Overlay */}
      <SearchOverlay 
        isOpen={showSearch} 
        onClose={() => setShowSearch(false)}
        initialQuery={searchQuery}
      />

      <LocationPickerSheet
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
      />

      {/* Header - compact on mobile */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-2.5 md:py-4">
          <div className="flex items-center justify-between">
            <Link to={SHOPPER_HOME}>
              <img src={FASH_LOGO} alt="Fash" className="h-16 md:h-20 object-contain" />
            </Link>

            {/* Mobile: inline search bar in header - opens overlay on focus */}
            <div className="flex-1 mx-3 md:hidden">
              <button
                onClick={() => setShowSearch(true)}
                className="w-full relative"
              >
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <div className="pl-9 h-9 rounded-full border border-gray-200 bg-gray-50 text-sm flex items-center text-gray-400">
                  Search...
                </div>
              </button>
            </div>

            <div className="flex items-center space-x-2 md:space-x-4">
              {user ? (
                <Link to="/settings" className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <Settings className="h-5 w-5 md:h-6 md:w-6 text-heading" />
                </Link>
              ) : (
                <Link to="/signin">
                  <Button variant="ghost" size="sm" className="text-xs md:text-sm px-2 md:px-3">Sign In</Button>
                </Link>
              )}
              <ShopperNavActions />
            </div>
          </div>
        </div>
      </header>

      <main className="px-3 md:px-6 max-w-7xl mx-auto">
        {!featuredLoading && featuredBoutiques.length > 0 && (
          <FeaturedStoreHeroCarousel boutiques={featuredBoutiques} />
        )}

        {/* Location bar - compact on mobile */}
        <section className="mt-2 md:mt-4 py-2 md:py-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowLocationPicker(true)}
              className="flex items-center gap-1.5 text-xs md:text-sm text-body hover:text-heading transition-colors"
              aria-label="Change delivery location"
            >
              <MapPin className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" />
              <span className="underline decoration-gray-300 underline-offset-2">
                {locationLoading && !shopperLocation ? 'Finding you...' : locationLabel}
              </span>
              <ChevronDown className="h-3 w-3 md:h-4 md:w-4 text-primary" />
            </button>
            <button className="flex items-center gap-1 text-xs md:text-sm text-body">
              <Clock className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" />
              <span>Same-day</span>
              <ChevronDown className="h-3 w-3 md:h-4 md:w-4 text-primary" />
            </button>
          </div>
        </section>

        {/* Desktop: search bar - opens overlay on click */}
        <section className="hidden md:block mt-4">
          <button
            onClick={() => setShowSearch(true)}
            className="w-full relative text-left"
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <div className="pl-12 h-14 rounded-full border border-gray-200 bg-white flex items-center text-gray-400 hover:border-gray-300 transition-colors">
              Search boutiques, brands, or styles...
            </div>
          </button>
        </section>

        {/* Boutiques Near You */}
        <section className="mt-3 md:mt-8 border-b border-gray-100 pb-3 md:pb-6">
          <div className="flex items-center justify-between mb-2.5 md:mb-6">
            <h3 className="text-base md:text-2xl font-serif text-heading">Boutiques Near You</h3>
            <button 
              onClick={() => navigate('/boutiques')}
              className="flex items-center text-xs md:text-sm font-medium text-primary hover:text-primary-hover transition-colors"
            >
              View All <ChevronRight className="h-3.5 w-3.5 md:h-4 md:w-4 ml-0.5" />
            </button>
          </div>
          
          {boutiquesLoading ? (
            <div className="flex space-x-3 md:space-x-6 overflow-x-auto pb-1">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex-shrink-0 flex flex-col items-center">
                  <div className="w-16 h-16 md:w-28 md:h-28 rounded-full bg-gray-100 animate-pulse" />
                  <div className="mt-1 w-12 h-3 bg-gray-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : boutiques.length === 0 ? (
            <div className="text-center py-6 text-sm text-body">
              <Store className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No boutiques deliver to {locationLabel} yet.</p>
              <button
                onClick={() => setShowLocationPicker(true)}
                className="mt-1 text-primary font-medium hover:underline"
              >
                Try a different address
              </button>
            </div>
          ) : (
            <div className="flex space-x-3 md:space-x-6 overflow-x-auto pb-1 scrollbar-hide">
              {boutiques.map((boutique) => (
                <button
                  key={boutique.id}
                  onClick={() => handleBoutiqueClick(boutique.id)}
                  className="flex-shrink-0 group cursor-pointer hover:opacity-90 transition-opacity"
                >
                  <div className="relative w-16 h-16 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-gray-200 group-hover:border-primary transition-colors bg-gray-100">
                    <FallbackImg 
                      src={boutique.logo || boutique.coverImage} 
                      alt={boutique.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  <p className="mt-1 md:mt-2 text-[11px] md:text-sm font-medium text-heading text-center truncate max-w-[64px] md:max-w-none">{boutique.name}</p>
                  {boutique.distanceMiles !== null && (
                    <p className="text-[10px] md:text-xs text-body text-center">
                      {formatMiles(boutique.distanceMiles)}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Trending Now */}
        <section className="mt-3 md:mt-8 pb-4 md:pb-8">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <div className="flex items-center gap-1.5">
              <Flame className="h-4 w-4 md:h-5 md:w-5 text-orange-500" />
              <h3 className="text-base md:text-2xl font-serif text-heading">Trending Now</h3>
            </div>
            {!showAllMobile && trendingProducts.length > MOBILE_PRODUCT_COUNT && (
              <button 
                onClick={() => setShowAllMobile(true)}
                className="md:hidden flex items-center text-xs font-medium text-primary hover:text-primary-hover transition-colors"
              >
                See All <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
              </button>
            )}
          </div>
          
          {/* Filter Pills */}
          <div className="flex space-x-1.5 md:space-x-2 mb-3 md:mb-6 overflow-x-auto scrollbar-hide">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3 md:px-4 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeFilter === filter
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-body hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
          
          {productsLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-100 rounded-lg" />
                  <div className="mt-2 h-3 bg-gray-100 rounded w-3/4" />
                  <div className="mt-1 h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : trendingProducts.length === 0 ? (
            <div className="text-center py-12 text-sm text-body">
              <p>No products available yet. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-6">
                {trendingProducts.map((product, index) => (
                  <div 
                    key={product.id} 
                    onClick={() => handleProductClick(product.id)} 
                    className={`cursor-pointer ${
                      !showAllMobile && index >= MOBILE_PRODUCT_COUNT ? 'hidden md:block' : ''
                    }`}
                  >
                    <ProductCard
                      product={{
                        id: product.id,
                        name: product.name,
                        brand: product.brand,
                        price: product.price,
                        compareAtPrice: product.compareAtPrice,
                        imageUrl: product.images?.[0] || '',
                        sizes: product.sizes,
                      }}
                      onAddToCart={(size, e) => {
                        e?.stopPropagation?.();
                        handleAddToCart(product.id, size);
                      }}
                      onFavorite={() => favorites.toggleFavorite({
                        productId: product.id,
                        name: product.name,
                        brand: product.brand,
                        price: product.price,
                        imageUrl: product.images?.[0] || '',
                      })}
                      isFavorited={favorites.isFavorited(product.id)}
                      compact
                    />
                  </div>
                ))}
              </div>

              {!showAllMobile && trendingProducts.length > MOBILE_PRODUCT_COUNT && (
                <button 
                  onClick={() => setShowAllMobile(true)}
                  className="md:hidden w-full mt-3 py-2.5 text-sm font-medium text-primary border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  See All Trending ({trendingProducts.length - MOBILE_PRODUCT_COUNT} more)
                </button>
              )}
            </>
          )}
        </section>

        {/* Promo Banner - Desktop only */}
        <section className="hidden md:block py-12 bg-heading rounded-2xl mb-8 -mx-4 md:-mx-6 px-4 md:px-6">
          <div className="text-center">
            <p className="text-white/60 uppercase tracking-widest text-sm mb-2">Stay in the Loop</p>
            <h3 className="text-3xl font-serif text-white mb-4">Get Updates, Exclusive Offers & Discounts</h3>
            <p className="text-white/80 mb-6">When you join our email list</p>
            <Button 
              className="bg-white text-heading hover:bg-gray-100"
              onClick={() => navigate('/boutiques')}
            >
              Shop Now
            </Button>
          </div>
        </section>
      </main>

      {/* Footer - hidden on mobile for single-page feel */}
      <ShopperFooter className="hidden md:block" />

      <ShopperBottomNav />
    </div>
  );
}
